import React, { useState, useEffect } from 'react';
import { Mail, Plus, Trash2, Plug, DownloadCloud, RefreshCw, Pencil, X, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getPositions, getMentionableUsers } from '../api';
import { User, Position } from '../types';

interface Props {
  currentUser: User;
}

interface MailAccount {
  id: string;
  owner_user_id: string;
  owner_name?: string;
  label: string;
  imap_host: string;
  imap_port: number;
  imap_secure: number;
  imap_user: string;
  has_pass: boolean;
  sender_filter: string;
  subject_filter: string;
  search_days: number;
  default_position_id: string | null;
  default_position_name?: string;
  enabled: number;
  last_poll_at?: string;
}

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

const emptyForm = {
  id: '', label: '', imap_host: '', imap_port: 993, imap_secure: 1,
  imap_user: '', imap_pass: '', sender_filter: '', subject_filter: '应聘,简历,投递',
  search_days: 3, default_position_id: '', enabled: 1, owner_user_id: '',
};

export default function MailAccounts({ currentUser }: Props) {
  const isAdmin = currentUser.role === 'admin';
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [accs, pos] = await Promise.all([
        api.get('/mail-intake/accounts').then(r => r.data),
        getPositions().catch(() => []),
      ]);
      setAccounts(accs);
      setPositions(pos);
      if (isAdmin) {
        try {
          const us = await getMentionableUsers();
          setUsers((us || []).filter((u: any) => ['admin', 'hr', 'vip'].includes(u.role)));
        } catch { /* ignore */ }
      }
    } catch (e: any) {
      toast.error('加载邮箱账户失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }

  const set = (k: string, v: any) => setForm((p: any) => ({ ...p, [k]: v }));

  function openAdd() {
    setForm({ ...emptyForm, owner_user_id: currentUser.id });
    setShowForm(true);
  }

  function openEdit(a: MailAccount) {
    setForm({
      id: a.id, label: a.label, imap_host: a.imap_host, imap_port: a.imap_port,
      imap_secure: a.imap_secure, imap_user: a.imap_user, imap_pass: '',
      sender_filter: a.sender_filter, subject_filter: a.subject_filter,
      search_days: a.search_days, default_position_id: a.default_position_id || '',
      enabled: a.enabled, owner_user_id: a.owner_user_id,
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.imap_host || !form.imap_user) { toast.error('请填写 IMAP 服务器与账号'); return; }
    if (!form.id && !form.imap_pass) { toast.error('请填写授权码'); return; }
    setSaving(true);
    try {
      const body = { ...form };
      if (!isAdmin) delete body.owner_user_id;
      if (form.id) {
        await api.put(`/mail-intake/accounts/${form.id}`, body);
      } else {
        await api.post('/mail-intake/accounts', body);
      }
      toast.success('已保存');
      setShowForm(false);
      await load();
    } catch (e: any) {
      toast.error('保存失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(a: MailAccount) {
    if (!window.confirm(`确认删除邮箱「${a.label || a.imap_user}」？`)) return;
    try {
      await api.delete(`/mail-intake/accounts/${a.id}`);
      toast.success('已删除');
      await load();
    } catch (e: any) {
      toast.error('删除失败: ' + (e.response?.data?.message || e.message));
    }
  }

  async function handleTest(a: MailAccount) {
    setBusyId(a.id + ':test');
    try {
      const r = await api.post(`/mail-intake/accounts/${a.id}/test`).then(res => res.data);
      toast.success(r.message || '连接成功');
    } catch (e: any) {
      toast.error('连接失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusyId('');
    }
  }

  async function handleFetch(a: MailAccount) {
    setBusyId(a.id + ':fetch');
    try {
      const r = await api.post(`/mail-intake/accounts/${a.id}/fetch-now`).then(res => res.data);
      toast.success(r.message || '拉取完成');
      await load();
    } catch (e: any) {
      toast.error('拉取失败: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusyId('');
    }
  }

  async function toggleEnabled(a: MailAccount) {
    try {
      await api.put(`/mail-intake/accounts/${a.id}`, { enabled: a.enabled ? 0 : 1 });
      await load();
    } catch (e: any) {
      toast.error('操作失败: ' + (e.response?.data?.message || e.message));
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-primary-600" />
          <div>
            <h2 className="font-semibold text-gray-800 text-sm">邮箱账户</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isAdmin ? '管理所有HR的投递邮箱；每个邮箱归属一位HR，简历自动归属并推送对应HR' : '管理你自己的投递邮箱，简历自动归到你名下'}
            </p>
          </div>
        </div>
        <button onClick={openAdd} className="flex items-center gap-1.5 text-xs font-medium bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700">
          <Plus size={14} /> 添加邮箱
        </button>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-8 justify-center">
            <RefreshCw size={16} className="animate-spin" /><span>加载中...</span>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center text-gray-400 py-8 text-sm">还没有邮箱账户，点击右上角「添加邮箱」开始</div>
        ) : (
          <div className="space-y-2.5">
            {accounts.map(a => (
              <div key={a.id} className="border border-gray-200 rounded-lg px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-800 text-sm">{a.label || a.imap_user}</span>
                      <button
                        onClick={() => toggleEnabled(a)}
                        className={`text-[11px] px-1.5 py-0.5 rounded-full ${a.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                      >
                        {a.enabled ? <span className="inline-flex items-center gap-1"><CheckCircle size={10} />已启用</span> : <span className="inline-flex items-center gap-1"><AlertCircle size={10} />已停用</span>}
                      </button>
                      {isAdmin && a.owner_name && (
                        <span className="text-[11px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">归属：{a.owner_name}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 mt-1 truncate">
                      {a.imap_user} @ {a.imap_host}:{a.imap_port}
                      {a.default_position_name ? ` · 默认岗位：${a.default_position_name}` : ''}
                      {a.last_poll_at ? ` · 上次拉取：${a.last_poll_at}` : ' · 从未拉取'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => handleTest(a)} disabled={!!busyId} title="测试连接" className="p-1.5 text-gray-500 hover:text-primary-600 rounded disabled:opacity-40">
                      {busyId === a.id + ':test' ? <RefreshCw size={14} className="animate-spin" /> : <Plug size={14} />}
                    </button>
                    <button onClick={() => handleFetch(a)} disabled={!!busyId} title="立即拉取" className="p-1.5 text-gray-500 hover:text-primary-600 rounded disabled:opacity-40">
                      {busyId === a.id + ':fetch' ? <RefreshCw size={14} className="animate-spin" /> : <DownloadCloud size={14} />}
                    </button>
                    <button onClick={() => openEdit(a)} title="编辑" className="p-1.5 text-gray-500 hover:text-primary-600 rounded"><Pencil size={14} /></button>
                    <button onClick={() => handleDelete(a)} title="删除" className="p-1.5 text-gray-500 hover:text-red-500 rounded"><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-800">{form.id ? '编辑邮箱' : '添加邮箱'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">备注名称</label>
                <input type="text" value={form.label} onChange={e => set('label', e.target.value)} placeholder="如 张三的Boss邮箱" className={inputCls} />
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">归属 HR</label>
                  <select value={form.owner_user_id} onChange={e => set('owner_user_id', e.target.value)} className={inputCls}>
                    <option value="">（默认归属自己）</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.display_name}（{u.role}）</option>)}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-0.5">该邮箱拉取的简历将归属此HR，晨报也推送给TA</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">IMAP 服务器</label>
                  <input type="text" value={form.imap_host} onChange={e => set('imap_host', e.target.value)} placeholder="imap.qq.com" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">端口</label>
                  <input type="number" value={form.imap_port} onChange={e => set('imap_port', e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">邮箱账号</label>
                <input type="text" value={form.imap_user} onChange={e => set('imap_user', e.target.value)} placeholder="you@company.com" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  授权码 {form.id && <span className="text-[11px] text-green-600 font-normal">（留空则不修改）</span>}
                </label>
                <input type="password" value={form.imap_pass} onChange={e => set('imap_pass', e.target.value)} placeholder={form.id ? '留空则沿用已有授权码' : 'IMAP 授权码（非登录密码）'} className={inputCls} />
                <p className="text-[11px] text-gray-400 mt-0.5">QQ/163 等需在邮箱设置开启 IMAP 并获取授权码</p>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={form.imap_secure === 1 || form.imap_secure === true} onChange={e => set('imap_secure', e.target.checked ? 1 : 0)} />
                  使用 SSL（993）
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={form.enabled === 1 || form.enabled === true} onChange={e => set('enabled', e.target.checked ? 1 : 0)} />
                  启用此邮箱
                </label>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">默认岗位（标题无法匹配时使用，可选）</label>
                <select value={form.default_position_id} onChange={e => set('default_position_id', e.target.value)} className={inputCls}>
                  <option value="">不指定</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">发件人关键词（逗号分隔，留空=不过滤）</label>
                <input type="text" value={form.sender_filter} onChange={e => set('sender_filter', e.target.value)} placeholder="zhipin.com,maimai.cn" className={inputCls} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">标题关键词（逗号分隔）</label>
                  <input type="text" value={form.subject_filter} onChange={e => set('subject_filter', e.target.value)} placeholder="应聘,简历,投递" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">回溯天数</label>
                  <input type="number" value={form.search_days} onChange={e => set('search_days', e.target.value)} className={inputCls} />
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="px-4 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">取消</button>
              <button onClick={handleSave} disabled={saving} className="px-5 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
